import * as React from 'react'

import { Trash2Icon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Kbd } from '~/client/src/shared/components/shadcn-ui/kbd'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import type { IStylePreview } from './MarkupEditorStore'
import {
  ColorSegment,
  FillSegment,
  SEGMENT,
  WeightSegment,
} from './markupSegments'
import MarkupToolButton from './MarkupToolButton'
import {
  DrawType,
  DRAW_TYPES,
  FillStyle,
  LineType,
  LINE_TYPES,
  MarkupTool,
  ShapeKind,
  SHAPE_KINDS,
} from './markupTools'

interface IProps {
  activeTool: MarkupTool
  color: string
  strokeWidth: number
  shapeKind: ShapeKind
  lineType: LineType
  drawType: DrawType
  fillColor: string
  fillStyle: FillStyle
  onColorChange: (color: string) => void
  onStrokeWidthChange: (width: number) => void
  onShapeKindChange: (kind: ShapeKind) => void
  onLineTypeChange: (type: LineType) => void
  onDrawTypeChange: (type: DrawType) => void
  // Click the active sub-tool chip to bail back to Select (no-Esc escape).
  onResetToSelect: () => void
  onFillColorChange: (color: string) => void
  onFillStyleChange: (style: FillStyle) => void
  polygonDrawing: boolean
  onFinishPolygon: () => void
  onCancelPolygon: () => void
  polylineDrawing: boolean
  onFinishPolyline: () => void
  onCancelPolyline: () => void
  // Touch: the Finish button keeps its label but drops the keyboard "Enter" keycap.
  isMobileEnv?: boolean
  // Mobile only: live-preview commit controls for the picker bottom sheets.
  stylePreview?: IStylePreview
}

function OptionsSegment<T extends string>(props: {
  options: {
    value: T
    icon: React.ComponentProps<typeof MarkupToolButton>['icon']
    label: string
    shortcut?: string
  }[]
  active: T
  onSelect: (value: T) => void
  onToggleOff?: () => void
}): JSX.Element {
  return (
    <div className={SEGMENT}>
      {props.options.map(({ value, icon, label, shortcut }) => (
        <MarkupToolButton
          key={value}
          icon={icon}
          label={label}
          shortcut={shortcut}
          isActive={props.active === value}
          onClick={() =>
            props.active === value && props.onToggleOff
              ? props.onToggleOff()
              : props.onSelect(value)
          }
        />
      ))}
    </div>
  )
}

const MarkupSubToolbar: React.FC<IProps> = props => {
  if (props.activeTool === 'select' || props.activeTool === 'text') return null

  // While drawing a polygon/polyline, the shape controls give way to Finish/Trash.
  // Enter also finishes and Escape discards (no need to travel to the button).
  const finishControls =
    props.activeTool === 'shape' &&
    props.shapeKind === 'polygon' &&
    props.polygonDrawing
      ? {
          label: Localization.translator.finishShaping,
          discard: Localization.translator.discardShape,
          onFinish: props.onFinishPolygon,
          onCancel: props.onCancelPolygon,
        }
      : props.activeTool === 'arrow' && props.polylineDrawing
        ? {
            label: Localization.translator.finishLine,
            discard: Localization.translator.discardLine,
            onFinish: props.onFinishPolyline,
            onCancel: props.onCancelPolyline,
          }
        : null
  if (finishControls) {
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-popover tw:p-1 tw:shadow-lg">
        <Button type="button" onClick={finishControls.onFinish}>
          {finishControls.label}
          {!props.isMobileEnv && (
            <Kbd className="tw:ml-2 tw:bg-primary-foreground/20 tw:text-primary-foreground">
              Enter
            </Kbd>
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={finishControls.discard}
          onClick={finishControls.onCancel}
          className="tw:text-destructive tw:hover:text-destructive"
        >
          <Trash2Icon aria-hidden="true" />
        </Button>
      </div>
    )
  }

  return (
    <div className="tw:flex tw:items-stretch tw:rounded-lg tw:bg-popover tw:shadow-lg">
      {/* Shape → Fill control; eraser → thickness only; else a stroke color. */}
      {props.activeTool === 'shape' ? (
        <FillSegment
          color={props.fillColor}
          fillStyle={props.fillStyle}
          onColorChange={props.onFillColorChange}
          onFillStyleChange={props.onFillStyleChange}
          isMobileEnv={props.isMobileEnv}
          stylePreview={props.stylePreview}
        />
      ) : props.activeTool === 'draw' && props.drawType === 'eraser' ? null : (
        <ColorSegment
          color={props.color}
          onChange={props.onColorChange}
          mode={
            props.activeTool === 'draw' && props.drawType === 'highlighter'
              ? 'wash'
              : 'opaque'
          }
          isMobileEnv={props.isMobileEnv}
          stylePreview={props.stylePreview}
        />
      )}

      {props.activeTool === 'draw' && (
        <WeightSegment
          width={props.strokeWidth}
          onChange={props.onStrokeWidthChange}
          isMobileEnv={props.isMobileEnv}
          stylePreview={props.stylePreview}
        />
      )}

      {props.activeTool === 'shape' && (
        <OptionsSegment
          options={SHAPE_KINDS}
          active={props.shapeKind}
          onSelect={props.onShapeKindChange}
          onToggleOff={props.onResetToSelect}
        />
      )}
      {props.activeTool === 'arrow' && (
        <OptionsSegment
          options={LINE_TYPES}
          active={props.lineType}
          onSelect={props.onLineTypeChange}
          onToggleOff={props.onResetToSelect}
        />
      )}
      {props.activeTool === 'draw' && (
        <OptionsSegment
          options={DRAW_TYPES}
          active={props.drawType}
          onSelect={props.onDrawTypeChange}
          onToggleOff={props.onResetToSelect}
        />
      )}
    </div>
  )
}

export default MarkupSubToolbar
